import type { FilterMenuRef } from '../viewOptions/filterMenu/FilterMenu';
import type { ReactElement } from 'react';
import type { InsightsMethodMap } from 'search-insights';

import { useRef, useMemo, useEffect } from 'react';
import { useCurrentRefinements, useStats } from 'react-instantsearch';
import styled, { css } from 'styled-components';

import { NoResultNotice } from '../../search/noResultsNotice/NoResultNotice';
import { ListerTitle } from '../../search/title/Title';
import { media } from '../../shared/core/styles';
import { InfiniteProductHits } from '../listing/InfiniteProductHits';
import { AppliedFiltersContainer } from '../viewOptions/filterMenu/AppliedFiltersContainer';
import { FilterMenu } from '../viewOptions/filterMenu/FilterMenu';
import { SearchMetadataBar } from '../viewOptions/filterMenu/SearchMetadataBar';
import { useAlgoliaInsights } from '~/components/shared/vendors/useAlgoliaInsights';
import { useVisibleFiltersCalculation } from '~/hooks/useVisibleFiltersCalculation';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { QuickViewContextProvider } from '~/utilities/context/dynamic/QuickViewContext';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { useAlgoliaLoadingState } from '~/utilities/hooks/search/useAlgoliaLoadingState';
import { useAppliedFacets } from '~/utilities/hooks/search/useAppliedFacets';

const CONFIG = {
  LAYOUT: {
    FILTER_BAR_HEIGHT: 26,
  },
  SCROLL: {
    FADE_THRESHOLD: 5,
  },
  CSS_VARIABLES: {
    FILTER_TOP_OFFSET: 'calc(var(--smart-banner-height) + var(--top-nav-height))',
    BANNER_ADJUSTED_OFFSET: 'calc(var(--filter-top-offset) + var(--usp-bar-height))',
  },
  Z_INDEX: {
    FILTER_BAR: 3,
  },
} as const;

const EDGE_FADE_UTILS = {
  updateFades: (container: HTMLUListElement, wrapper: HTMLUListElement) => {
    const maxScroll = container.scrollWidth - container.clientWidth;
    const left = container.scrollLeft;

    wrapper.setAttribute('data-fade-left', String(left > CONFIG.SCROLL.FADE_THRESHOLD));
    wrapper.setAttribute(
      'data-fade-right',
      String(left < maxScroll - CONFIG.SCROLL.FADE_THRESHOLD)
    );
  },
} as const;

const S = {
  Section: styled.section<{ $noResults: boolean }>`
    ${({ theme, $noResults }) => css`
      display: flex;
      flex-direction: column;
      background-color: ${theme.colors.background.base};
      scroll-margin-top: calc(var(--top-nav-height) + var(--smart-banner-height));

      #uspBar ~ * & {
        scroll-margin-top: calc(
          var(--top-nav-height) + var(--smart-banner-height) + var(--usp-bar-height)
        );
      }

      ${$noResults &&
      css`
        max-width: 956px;
        margin: 0;
        padding: 0;
        background-color: transparent;
      `}
    `}
  `,

  FilterBar: styled.div`
    ${({ theme }) => css`
      display: flex;
      height: ${CONFIG.LAYOUT.FILTER_BAR_HEIGHT}px;
      padding: ${theme.padding.sm} ${theme.padding['2xs']} ${theme.padding.xs};
      justify-content: space-between;
      align-items: center;
      background-color: ${theme.colors.background.base};

      @media ${media(theme).greaterThan('sm')} {
        padding: ${theme.padding.md} ${theme.padding.sm} ${theme.padding.sm} ${theme.padding.sm};
      }

      @media ${media(theme).greaterThan('desktop-sm')} {
        padding-inline: ${theme.padding.md};
      }

      --filter-top-offset: ${CONFIG.CSS_VARIABLES.FILTER_TOP_OFFSET};

      position: sticky;
      top: var(--filter-top-offset);
      z-index: ${CONFIG.Z_INDEX.FILTER_BAR};

      #uspBar ~ * & {
        top: ${CONFIG.CSS_VARIABLES.BANNER_ADJUSTED_OFFSET};
      }
    `}
  `,
};

export const ProductListSection = (): ReactElement => {
  const { nbHits } = useStats();
  const { isFirstLoading } = useAlgoliaLoadingState();
  const { isPLP } = useProductListContext();
  const { isMobile } = useMediaQueryContext();
  const { numberOfAppliedFacets } = useAppliedFacets();
  const { items: appliedAlgoliaFacets } = useCurrentRefinements();
  const { pushToAlgoliaWithQueryId } = useAlgoliaInsights();

  const filterBarRef = useRef<HTMLDivElement>(null);
  const hasTrackedViewedFiltersRef = useRef(false);

  useEffect(() => {
    if (!isFirstLoading) {
      hasTrackedViewedFiltersRef.current = false;

      return;
    }

    if (hasTrackedViewedFiltersRef.current || appliedAlgoliaFacets.length === 0) {
      return;
    }

    const filters = appliedAlgoliaFacets.flatMap(facet =>
      facet.refinements.map(refinement => `${refinement.attribute}:${refinement.value.toString()}`)
    );

    if (filters.length === 0) {
      return;
    }

    const event: keyof InsightsMethodMap = 'viewedFilters';
    const eventName = isPLP ? 'PLP_viewedFilters' : 'PRP_viewedFilters';

    hasTrackedViewedFiltersRef.current = true;
    pushToAlgoliaWithQueryId(event, eventName, { filters });
  }, [appliedAlgoliaFacets, isFirstLoading, isPLP, pushToAlgoliaWithQueryId]);

  const isSearchNoResult = useMemo(
    () => !isPLP && nbHits === 0 && !isFirstLoading,
    [isPLP, nbHits, isFirstLoading]
  );

  const { visibleFiltersCount, hasOverflow, scrollContainerRef } =
    useVisibleFiltersCalculation<HTMLUListElement>({
      enabled: !isFirstLoading,
      isMobile,
      numberOfAppliedFacets,
    });

  const filterMenuRef = useRef<FilterMenuRef | null>(null);

  useEffect(() => {
    const el = scrollContainerRef.current;

    if (!el) {
      return undefined;
    }

    const update = () => {
      EDGE_FADE_UTILS.updateFades(el, el);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });

    const resizeObserver = new ResizeObserver(update);

    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [isFirstLoading, isMobile, numberOfAppliedFacets, scrollContainerRef]);

  const appliedFilters = isFirstLoading ? null : (
    <AppliedFiltersContainer
      isMobile={isMobile}
      hasOverflow={hasOverflow}
      visibleFiltersCount={visibleFiltersCount}
      scrollContainerRef={scrollContainerRef}
      filterMenuRef={filterMenuRef}
    />
  );

  return (
    <QuickViewContextProvider>
      {!isPLP && <ListerTitle isSearchNoResult={isSearchNoResult} />}

      <S.Section $noResults={isSearchNoResult} data-plp-section>
        {isSearchNoResult ? (
          <NoResultNotice />
        ) : (
          <>
            <S.FilterBar ref={filterBarRef}>
              <SearchMetadataBar />

              {!isMobile && appliedFilters}

              <FilterMenu ref={filterMenuRef} filterBarRef={filterBarRef} />
            </S.FilterBar>
            {isMobile && appliedFilters}
            <InfiniteProductHits />
          </>
        )}
      </S.Section>
    </QuickViewContextProvider>
  );
};
